import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { connection, TREASURY_ADDRESS, fetchPoolFeeDetails, MIN_CLAIMABLE_QUOTE_FEE_LAMPORTS } from '@/lib/solana'
import { fetchDbcConfig } from '@/lib/dbcConfig'
import { fetchDammV2PositionFees, type DammV2PoolLabel, type DammV2PositionFees } from '@/lib/dammV2'

const LAMPORTS_PER_SOL = 1_000_000_000

export interface TokenInfo {
  mintAddress: string
  name: string
  symbol: string
  poolAddress: string
}

export interface TradingFeePool {
  token: TokenInfo
  claimableLamports: number
  claimableSOL: number
}

export interface MigrationFeePool {
  token: TokenInfo
  isMigrated: boolean
  partnerWithdrawn: boolean
  creatorWithdrawn: boolean
  dammPoolAddress: string | null
}

export interface AutoCollectedMigrationFee {
  token: TokenInfo
  migratedAt: string | null
  migrationTx: string | null
  estimatedFeeSOL: number
}

export interface TreasuryFeesData {
  tradingFees: TradingFeePool[]
  tradingFeeTotalSOL: number
  tradingFeePoolCount: number
  migrationPools: MigrationFeePool[]
  autoCollectedMigrationFees: AutoCollectedMigrationFee[]
  autoCollectedTotalSOL: number
  dammV2Positions: DammV2PositionFees[]
  claimHistory: ClaimRecord[]
}

export interface ClaimRecord {
  id: string
  createdAt: string
  poolAddress: string
  tokenMint: string
  claimType: string
  amountLamportsQuote: number
  signature: string | null
  status: string
}

/**
 * Comprehensive hook for all treasury fee data.
 * Fetches pool addresses from DB, reads on-chain fee data,
 * and joins with token metadata.
 *
 * ------------------------------------------------------------------
 * RELATIONSHIP TO THE DASHBOARD "dextoro Revenue" CARD (IMPORTANT)
 * ------------------------------------------------------------------
 *
 * This hook feeds the Treasury page only (super_admin). The two numbers a
 * reader is most likely to misinterpret are derived from its output:
 *
 *   1. "Total Claimed (All-time)" (computed in Treasury.tsx)
 *      = sum of every admin_fee_claims row where status in ('confirmed','success'),
 *        regardless of claim_type (BC trading, migration, damm_v2_position_fee).
 *      = only money that was explicitly claimed via an admin transaction.
 *
 *   2. `autoCollectedTotalSOL` (computed below at line ~186)
 *      = graduatedCount * config.migrationFeePerGraduationSOL
 *      = an ESTIMATE of migration fees based on graduation count. It does not
 *        check whether those fees were subsequently claimed.
 *
 * These two numbers are NOT additive with the Dashboard's "dextoro Revenue."
 * `autoCollectedTotalSOL` is the same term as `migrationRevenuePeriod` in
 * `useProtocolRevenue.ts` (for period='all') and is already included in the
 * dashboard total. Adding them double-counts migration fees and still omits
 * all lifetime bonding-curve trading revenue, which only the trades-table sum
 * in useProtocolRevenue captures.
 *
 * See `useProtocolRevenue.ts` for the full equation. An investor who added
 * Total Claimed (14.76) + Auto-collected (17.58) in April 2026 concluded the
 * company had earned ~$960 in two years and damaged the angel round pitch.
 */
export function useTreasuryFees() {
  return useQuery({
    queryKey: ['treasury-fees'],
    queryFn: async (): Promise<TreasuryFeesData> => {
      // 1. Get all launched tokens with pool addresses
      const { data: tokens, error: tokensError } = await supabase
        .from('launched_tokens')
        .select('mint_address, name, symbol, pool_address')
        .not('pool_address', 'is', null)

      if (tokensError) {
        console.error('[TreasuryFees] tokens query error:', tokensError)
      }

      const tokenList: TokenInfo[] = (tokens || [])
        .filter((t: { pool_address: string | null }) => t.pool_address)
        .map((t: { mint_address: string; name: string; symbol: string; pool_address: string }) => ({
          mintAddress: t.mint_address,
          name: t.name,
          symbol: t.symbol,
          poolAddress: t.pool_address,
        }))

      const poolAddresses = tokenList.map(t => t.poolAddress)
      const tokensByPool = new Map(tokenList.map(t => [t.poolAddress, t]))

      // 2. Fetch on-chain pool fee details (batched)
      const poolDetails = poolAddresses.length > 0
        ? await fetchPoolFeeDetails(poolAddresses)
        : []

      // 3. Build trading fees list
      const tradingFees: TradingFeePool[] = []
      let tradingFeeTotalLamports = 0
      let tradingFeePoolCount = 0

      for (const pool of poolDetails) {
        const token = tokensByPool.get(pool.poolAddress)
        if (!token) continue

        if (pool.partnerQuoteFeeLamports >= MIN_CLAIMABLE_QUOTE_FEE_LAMPORTS) {
          tradingFees.push({
            token,
            claimableLamports: pool.partnerQuoteFeeLamports,
            claimableSOL: pool.partnerQuoteFeeLamports / LAMPORTS_PER_SOL,
          })
          tradingFeeTotalLamports += pool.partnerQuoteFeeLamports
          tradingFeePoolCount++
        }
      }

      // Sort by claimable amount descending
      tradingFees.sort((a, b) => b.claimableLamports - a.claimableLamports)

      // 4. Build migration pools list from launched_tokens migration columns
      const { data: migratedTokens } = await supabase
        .from('launched_tokens')
        .select('mint_address, name, symbol, pool_address, damm_pool_address, treasury_position_address, treasury_position_nft_mint, creator_position_address, creator_position_nft_mint, migrated_at, migration_tx')
        .eq('is_migrated', true)

      const migrationPools: MigrationFeePool[] = []
      const dammV2Pools: DammV2PoolLabel[] = []

      for (const row of migratedTokens || []) {
        const token = tokensByPool.get(row.pool_address)
        // Find on-chain migration status for this pool
        const poolData = poolDetails.find(p => p.poolAddress === row.pool_address)
        const partnerWithdrawn = poolData ? (poolData.migrationFeeWithdrawStatus & 0b100) !== 0 : false
        const creatorWithdrawn = poolData ? (poolData.migrationFeeWithdrawStatus & 0b010) !== 0 : false

        migrationPools.push({
          token: token || { mintAddress: row.mint_address, name: row.name, symbol: row.symbol, poolAddress: row.pool_address },
          isMigrated: true,
          partnerWithdrawn,
          creatorWithdrawn,
          dammPoolAddress: row.damm_pool_address || null,
        })

        // Any graduated token with a DAMM V2 pool goes to on-chain discovery.
        // Position address and NFT mint are resolved on-chain, not from DB columns.
        if (row.damm_pool_address) {
          dammV2Pools.push({
            label: row.name,
            symbol: row.symbol,
            dammPoolAddress: row.damm_pool_address,
          })
        }
      }

      // 5. Build auto-collected migration fees from on-chain config
      let autoCollectedMigrationFees: AutoCollectedMigrationFee[] = []
      let autoCollectedTotalSOL = 0
      try {
        const config = await fetchDbcConfig()
        const feePerGraduation = config.migrationFeePerGraduationSOL

        autoCollectedMigrationFees = (migratedTokens || []).map((row: {
          mint_address: string
          name: string
          symbol: string
          pool_address: string
          migrated_at: string | null
          migration_tx: string | null
        }) => ({
          token: tokensByPool.get(row.pool_address) || {
            mintAddress: row.mint_address,
            name: row.name,
            symbol: row.symbol,
            poolAddress: row.pool_address,
          },
          migratedAt: row.migrated_at || null,
          migrationTx: row.migration_tx || null,
          estimatedFeeSOL: feePerGraduation,
        }))

        // Sort by migration date descending (most recent first)
        autoCollectedMigrationFees.sort((a, b) => {
          if (!a.migratedAt) return 1
          if (!b.migratedAt) return -1
          return new Date(b.migratedAt).getTime() - new Date(a.migratedAt).getTime()
        })

        autoCollectedTotalSOL = autoCollectedMigrationFees.length * feePerGraduation
      } catch (err) {
        console.error('[TreasuryFees] Failed to fetch DBC config for auto-collected fees:', err)
      }

      // 6. Discover DAMM V2 positions on-chain and compute claimable fees.
      // Position address and NFT account are resolved via CpAmm SDK, which
      // handles locked positions (escrow PDAs) automatically.
      const dammV2Positions = dammV2Pools.length > 0
        ? await fetchDammV2PositionFees(connection, TREASURY_ADDRESS, dammV2Pools)
        : []

      // 7. Fetch claim history
      const { data: claims, error: claimsError } = await supabase
        .from('admin_fee_claims')
        .select('id, created_at, pool_address, token_mint, claim_type, amount_lamports_quote, signature, status')
        .order('created_at', { ascending: false })
        .limit(1000)

      if (claimsError) {
        console.error('[TreasuryFees] claims query error:', claimsError)
      }

      const claimHistory: ClaimRecord[] = (claims || []).map((c: {
        id: string
        created_at: string
        pool_address: string
        token_mint: string
        claim_type: string
        amount_lamports_quote: number
        signature: string | null
        status: string
      }) => ({
        id: c.id,
        createdAt: c.created_at,
        poolAddress: c.pool_address,
        tokenMint: c.token_mint,
        claimType: c.claim_type,
        amountLamportsQuote: Number(c.amount_lamports_quote) || 0,
        signature: c.signature,
        status: c.status,
      }))

      return {
        tradingFees,
        tradingFeeTotalSOL: tradingFeeTotalLamports / LAMPORTS_PER_SOL,
        tradingFeePoolCount,
        migrationPools,
        autoCollectedMigrationFees,
        autoCollectedTotalSOL,
        dammV2Positions,
        claimHistory,
      }
    },
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  })
}
